import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { OrderImportService, OrderImportStatus } from '../../services';
import { flatten } from '../../utils/flatten';
import { IOrderImportFilter } from '../../interfaces/order-import-filter';
import { Subject, Subscription } from 'rxjs';
import {
  IOrderImport,
  IOrderImportDetail,
  IOrderImportResult,
} from '../../interfaces/upload-order/order-import';
import { MessageService, SelectItem } from 'primeng';
import { debounceTime, map } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ISubscription } from 'rxjs-compat/Subscription';
import { BroadcastHubService } from '../../services/broadcast-hub.service';
import { BroadcastHubEventService } from '../../services/broadcast-hub-event.service';
import { BroadcastHubMessage } from '../../interfaces/broadcast-hub-message';
import { BroadcastHubMessageTypeEnum } from '../../enums/broadcast-hub-message-group.enum';
import { Message } from '../../classes/Message';

@Component({
  selector: 'order-validate',
  templateUrl: './order-validate.component.html',
  styleUrls: ['./order-validate.component.scss'],
  providers: [],
})
export class OrderValidateComponent implements OnInit, OnDestroy {
  cols: any;
  orderImports: SelectItem[] = [];
  isBusy: boolean;
  isLoading: boolean;
  public selectedOrderImport: IOrderImport;
  public orderImportFilterVal: IOrderImportFilter;
  readyForTransferStatus: number;
  isBusySubject: Subject<any>;
  busySubscription: ISubscription;
  isProcessing: boolean = false;
  processingStatus: number;
  orderDetailsForm: UntypedFormGroup;
  showOrderUpdateDialog: boolean = false;

  private eventSubscription: Subscription;

  public OrderImportStatus = OrderImportStatus;
  public requestingTransfer: boolean;
  public loadingImports: boolean;

  public get showEmptyView() {
    if (this.loadingImports) {
      return false;
    }
    return !this.orderImports || this.orderImports.length === 0;
  }

  constructor(
    private messageService: MessageService,
    private orderImportService: OrderImportService,
    private confirmationService: ConfirmationService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private broadcastHub: BroadcastHubService,
    private broadcastHubEventService: BroadcastHubEventService,
  ) {
    this.isBusySubject = new Subject<any>();
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
    if (this.busySubscription) this.busySubscription.unsubscribe();
  }

  async ngOnInit() {
    this.initialiseDefaultFilters();
    await this.load();
    this.cols = this.orderImportService.cols;
    this.readyForTransferStatus = OrderImportStatus.ReadyForTransfer;

    if (this.orderImports.length > 0) {
      void this.broadcastHub.subscribeToChannel(
        `OrderImport-${this.orderImports[0].value.customerId}`,
      );
    }

    this.eventSubscription = this.broadcastHubEventService.OrderImportEvent.subscribe((event) =>
      this.handleOrderImportEvent(event),
    );

    this.busySubscription = this.isBusySubject
      .pipe(
        debounceTime(1000),
        map((i: any) => {
          if (this.isLoading) {
            this.isBusy = true;
          }
        }), // show loading icon after an x number of secs if the request is still being expected
      )
      .subscribe((x) => {});
  }

  handleOrderImportEvent(event: BroadcastHubMessage<any>) {
    console.log('handleOrderImportEvent');
    const data = event.messageData;
    console.log(event);

    if (event.messageType === BroadcastHubMessageTypeEnum.OrderImportRowStatusChange) {
      if (this.selectedOrderImport && this.selectedOrderImport.id == data.orderImportId) {
        const rowIndex = this.selectedOrderImport.orderImportDetails.findIndex(
          (x) => x.id == data.orderImportDetailId,
        );

        if (rowIndex !== -1) {
          this.selectedOrderImport.orderImportDetails[rowIndex] = {
            ...this.selectedOrderImport.orderImportDetails[rowIndex],
            status: data.status,
            status_id: data.statusId,
            status_description: data.statusDescription,
            import_errors: data.importErrors,
          };
        }
      }
    }

    if (event.messageType === BroadcastHubMessageTypeEnum.OrderImportStatusChange) {
      const orderIndex = this.orderImports.findIndex((x) => x.value.id == data.orderImportId);

      if (orderIndex !== -1) {
        this.orderImports[orderIndex].value = {
          ...this.orderImports[orderIndex].value,
          status: {
            id: data.statusId,
            name: data.status,
            description: data.statusDescription,
          },
        };
      }

      if (this.selectedOrderImport && this.selectedOrderImport.id == data.orderImportId) {
        this.selectedOrderImport = {
          ...this.selectedOrderImport,
          status: {
            id: data.statusId,
            name: data.status,
            description: data.statusDescription,
          },
        };
      }
    }
  }

  initialiseDefaultFilters() {
    this.orderImportFilterVal = {
      first: 0,
      rows: null,
      sortField: null,
      sortOrder: 1,
      filters: {
        createdOnFrom: { value: null },
        createdOnTo: { value: null },
        status: {
          value: [
            OrderImportStatus.New,
            OrderImportStatus.ReadyForTransfer,
            OrderImportStatus.Invalid,
            OrderImportStatus.Valid,
            OrderImportStatus.ProcessingForTransfer,
            OrderImportStatus.Queued,
          ],
        },
        includeDetails: { value: null },
      },
    };
  }

  async load() {
    try {
      this.orderImports = [];
      this.loadingImports = true;
      const res = await this.orderImportService.getProcessingOrderImports(
        flatten(this.orderImportFilterVal),
      );
      const imports = res && res.length > 0 ? res.reverse() : [];

      imports.forEach((item) => {
        this.orderImports.push({ label: item.fileName, value: item });
      });

      if (res.length > 0) {
        this.changeSelectedOrder(res[0]);
      }
    } catch (err) {
      console.log(err);
      this.messageService.add({
        severity: 'error',
        summary: 'Fetching Order Imports',
        detail: `An error occurred while fetching imports`,
      });
    } finally {
      this.loadingImports = false;
    }
  }

  getOrderDetails(orderImportId: number) {
    this.isLoading = true;
    this.orderImportService
      .getOrderImportDetails(orderImportId)
      .then((res) => {
        this.selectedOrderImport.orderImportDetails = res;
        this.isLoading = false;
        this.isBusy = false;
      })
      .catch((err) => {
        console.log(err);
        this.isLoading = false;
        this.busySubscription.unsubscribe();
        this.messageService.add({
          severity: 'error',
          summary: 'Fetching Order Details',
          detail: `An error occurred while fetching details of this Order`,
        });
      });
  }

  removeImportDialog(orderImport: IOrderImport) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this order import?',
      header: 'Confirm Order Import Delete',
      accept: () => {
        this.removeImport(orderImport.id);
      },
    });
  }

  removeImport(id: number) {
    let errMsg = 'An error occurred while deleting imported Order';
    this.isBusy = true;
    this.orderImportService
      .removeOrderImport(id)
      .then(() => {
        this.removeFromState(id);
        this.messageService.add({
          severity: 'success',
          summary: 'Deleting Order Details',
          detail: `Order has been deleted successfully`,
        });
        this.isBusy = false;
      })
      .catch((err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Deleting Order Details',
          detail: `${errMsg}`,
        });
        this.isBusy = false;
      });
  }

  validateImport(orderImportId: number) {
    if (orderImportId) {
      this.isBusy = true;
      this.orderImportService
        .validate(orderImportId)
        .then((res) => {
          this.handleValidationResult(res);
          this.isBusy = false;
        })
        .catch((err) => {
          console.log(err);
          this.isBusy = false;
          this.handleValidationError(err);
          this.messageService.add({
            severity: 'error',
            summary: 'Validating Order Details',
            detail: `An error occurred while validating details of this Order`,
          });
        });
    }
  }

  handleValidationResult(res: IOrderImportResult) {
    let index = this.orderImports.findIndex((x) => x.value.id == res.orderImport.id);
    if (index > -1) {
      this.orderImports[index].value = this.selectedOrderImport = res.orderImport;
    }
    if (!res.isSuccess) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validating Order Details',
        detail: `There are invalid entries in the list`,
      });
    }
    if (res.warnings?.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validating Order Details',
        detail: `There are warnings for some entries in the list`,
      });
    }
    if (
      res.orderImport &&
      (!res.orderImport.orderImportDetails || res.orderImport.orderImportDetails.length < 1)
    ) {
      this.getOrderDetails(res.orderImport.id);
    }
  }

  handleValidationError(err: IOrderImportResult) {
    try {
      let orderImportRes: IOrderImportResult;
      if (typeof err === 'object' && typeof err == typeof orderImportRes) {
        let index = this.orderImports.findIndex((x) => x.value.id == err.orderImport.id);
        if (index > -1) {
          this.orderImports[index].value = this.selectedOrderImport = err.orderImport;
        }
      }
      if (
        err.orderImport &&
        (!err.orderImport.orderImportDetails || err.orderImport.orderImportDetails.length < 1)
      ) {
        this.getOrderDetails(err.orderImport.id);
      }
    } catch (exc) {
      console.log(exc);
    }
  }

  removeFromState(id) {
    if (this.selectedOrderImport && this.selectedOrderImport.id == id) {
      this.selectedOrderImport = null;
    }
    let index = this.orderImports.findIndex((x) => x.value.id == id);
    if (index > -1) {
      this.orderImports.splice(index, 1);
    }

    if (this.orderImports?.length > 1) {
      this.changeSelectedOrder(this.orderImports[0].value);
    }
  }

  async transferOrder(orderImport: IOrderImport) {
    try {
      this.requestingTransfer = true;
      await this.orderImportService.transferOrderImport(orderImport.id);
      this.requestingTransfer = false;
      void this.load();
    } catch (error) {
      this.messageService.add(Message.Error('Failed to process import'));
      this.requestingTransfer = false;
    }
  }

  editEntry(entry: IOrderImportDetail) {
    this.setOrderForm(entry);
    this.showOrderUpdateDialog = true;
  }

  removeEntry(entry: IOrderImportDetail) {
    var that = this;
    var errMsg = {
      severity: 'error',
      summary: 'Deleting Order Details',
      detail: `An error occurred while deleting order detail`,
    };
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this order entry?',
      header: 'Confirm Order Delete',
      accept: () => {
        this.orderImportService
          .removeImportDetail(entry.id)
          .then((res) => {
            that.handleDeleteImportDetail(entry);
          })
          .catch((err) => {
            this.messageService.add(errMsg);
          });
      },
    });
  }

  handleDeleteImportDetail(orderDetail: IOrderImportDetail) {
    this.selectedOrderImport.entriesCount--;

    if (this.selectedOrderImport.entriesCount > 0) {
      let indexOfEditedDetail = this.selectedOrderImport.orderImportDetails.findIndex(
        (x) => x.id == orderDetail.id,
      );
      if (indexOfEditedDetail > -1) {
        this.selectedOrderImport.orderImportDetails.splice(indexOfEditedDetail, 1);
        let temptable = this.selectedOrderImport;
        this.selectedOrderImport = null;
        setTimeout(() => (this.selectedOrderImport = temptable), 0);
      } else {
        this.getOrderDetails(this.selectedOrderImport.id);
      }
    } else {
      let indexOfImport = this.orderImports.findIndex(
        (x) => x.value.id == this.selectedOrderImport.id,
      );
      if (indexOfImport > -1) {
        if (this.orderImports.length > 1) {
          this.orderImports.splice(indexOfImport, 1);
        } else {
          this.orderImports = null;
        }
      }

      if (this.orderImports?.length > 1) {
        this.changeSelectedOrder(this.orderImports[0].value);
      } else {
        this.selectedOrderImport = null;
      }
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Order Deleted',
      detail: `The requested order import line has been deleted`,
    });
  }

  handleUpdateImportDetail(orderImportId: number, orderDetail: IOrderImportDetail) {
    let indexOfEditedDetail = this.selectedOrderImport.orderImportDetails.findIndex(
      (x) => x.id == orderDetail.id,
    );
    if (indexOfEditedDetail > -1) {
      this.validateImport(orderImportId);
    } else {
      this.getOrderDetails(this.selectedOrderImport.id);
    }
  }

  setOrderForm(orderImportDetail: IOrderImportDetail) {
    this.orderDetailsForm = this.fb.group({
      id: new UntypedFormControl(orderImportDetail.id),
      status: new UntypedFormControl(orderImportDetail.status),
      customer_name: new UntypedFormControl(orderImportDetail.customer_name, null),
      vehicle_reg: new UntypedFormControl(orderImportDetail.vehicle_reg, null),
      vin: new UntypedFormControl(orderImportDetail.vin, null),
      kerb_weight: new UntypedFormControl(orderImportDetail.kerb_weight, null),
      pick_up_business_name: new UntypedFormControl(orderImportDetail.pick_up_business_name, null),
      pick_up_address_line_1: new UntypedFormControl(
        orderImportDetail.pick_up_address_line_1,
        null,
      ),
      pick_up_address_line_2: new UntypedFormControl(
        orderImportDetail.pick_up_address_line_2,
        null,
      ),
      pick_up_address_line_3: new UntypedFormControl(
        orderImportDetail.pick_up_address_line_3,
        null,
      ),
      pick_up_town: new UntypedFormControl(orderImportDetail.pick_up_town, null),
      pick_up_postcode: new UntypedFormControl(orderImportDetail.pick_up_postcode, null),
      pick_up_earliest: new UntypedFormControl(
        orderImportDetail.pick_up_earliest ? new Date(orderImportDetail.pick_up_earliest) : null,
        null,
      ),
      pick_up_latest: new UntypedFormControl(
        orderImportDetail.pick_up_latest ? new Date(orderImportDetail.pick_up_latest) : null,
        null,
      ),
      pick_up_additional_requirements: new UntypedFormControl(
        orderImportDetail.pick_up_additional_requirements,
        null,
      ),
      pick_up_notes: new UntypedFormControl(orderImportDetail.pick_up_notes, null),
      pick_up_forename: new UntypedFormControl(orderImportDetail.pick_up_forename, null),
      pick_up_surname: new UntypedFormControl(orderImportDetail.pick_up_surname, null),
      pick_up_email: new UntypedFormControl(orderImportDetail.pick_up_email, null),
      pick_up_secondary_email: new UntypedFormControl(
        orderImportDetail.pick_up_secondary_email,
        null,
      ),
      pick_up_phone1: new UntypedFormControl(orderImportDetail.pick_up_phone1, null),
      pick_up_phone2: new UntypedFormControl(orderImportDetail.pick_up_phone2, null),
      drop_off_business_name: new UntypedFormControl(
        orderImportDetail.drop_off_business_name,
        null,
      ),
      drop_off_address_line_1: new UntypedFormControl(
        orderImportDetail.drop_off_address_line_1,
        null,
      ),
      drop_off_address_line_2: new UntypedFormControl(
        orderImportDetail.drop_off_address_line_2,
        null,
      ),
      drop_off_address_line_3: new UntypedFormControl(
        orderImportDetail.drop_off_address_line_3,
        null,
      ),
      drop_off_town: new UntypedFormControl(orderImportDetail.drop_off_town, null),
      drop_off_postcode: new UntypedFormControl(orderImportDetail.drop_off_postcode, null),
      drop_off_earliest: new UntypedFormControl(
        orderImportDetail.drop_off_earliest ? new Date(orderImportDetail.drop_off_earliest) : null,
        null,
      ),
      drop_off_latest: new UntypedFormControl(
        orderImportDetail.drop_off_latest ? new Date(orderImportDetail.drop_off_latest) : null,
        null,
      ),
      drop_off_additional_requirements: new UntypedFormControl(
        orderImportDetail.drop_off_additional_requirements,
        null,
      ),
      drop_off_notes: new UntypedFormControl(orderImportDetail.drop_off_notes, null),
      drop_off_forename: new UntypedFormControl(orderImportDetail.drop_off_forename, null),
      drop_off_surname: new UntypedFormControl(orderImportDetail.drop_off_surname, null),
      drop_off_email: new UntypedFormControl(orderImportDetail.drop_off_email, null),
      drop_off_secondary_email: new UntypedFormControl(
        orderImportDetail.drop_off_secondary_email,
        null,
      ),
      drop_off_phone1: new UntypedFormControl(orderImportDetail.drop_off_phone1, null),
      drop_off_phone2: new UntypedFormControl(orderImportDetail.drop_off_phone2, null),
      purchase_order_no: new UntypedFormControl(orderImportDetail.purchase_order_no, null),
      department_code: new UntypedFormControl(orderImportDetail.department_code, null),
      job_tagging: new UntypedFormControl(orderImportDetail.job_tagging, null),
      department: new UntypedFormControl(orderImportDetail.department, null),
      team: new UntypedFormControl(orderImportDetail.team, null),
      order_type: new UntypedFormControl(orderImportDetail.order_type, null),
      account_code: new UntypedFormControl(orderImportDetail.account_code, null),
      is_vehicle_taxed: new UntypedFormControl(orderImportDetail.is_vehicle_taxed, null),
      is_mot_valid: new UntypedFormControl(orderImportDetail.is_mot_valid, null),
      customer_order_number: new UntypedFormControl(orderImportDetail.customer_order_number, null),
      user_email_address: new UntypedFormControl(orderImportDetail.user_email_address, null),
      make: new UntypedFormControl(orderImportDetail.make, null),
      model: new UntypedFormControl(orderImportDetail.model, null),
      hold_period: new UntypedFormControl(
        orderImportDetail.hold_period
          ? new Date(orderImportDetail.hold_period)
          : orderImportDetail.hold_period,
        null,
      ),
      step_group_name: new UntypedFormControl(orderImportDetail.step_group_name, null),
      enforce_vrm_validation: new UntypedFormControl(
        orderImportDetail.enforce_vrm_validation,
        null,
      ),
      customer_service_fees: new UntypedFormControl(orderImportDetail.customer_service_fees, null),
      pickup_customer_site_name: new UntypedFormControl(
        orderImportDetail.pickup_customer_site_name,
        null,
      ),
      drop_off_customer_site_name: new UntypedFormControl(
        orderImportDetail.drop_off_customer_site_name,
        null,
      ),
      moDel_pin: new UntypedFormControl(orderImportDetail.moDel_pin, null),
      release_code: new UntypedFormControl(orderImportDetail.release_code, null),
      fuel_type: new UntypedFormControl(orderImportDetail.fuel_type, null),
      combine_group_code: new UntypedFormControl(orderImportDetail.combine_group_code, null),
      what_3_words_pick_up_address: new UntypedFormControl(
        orderImportDetail.what_3_words_pick_up_address,
        null,
      ),
      what_3_words_drop_off_address: new UntypedFormControl(
        orderImportDetail.what_3_words_drop_off_address,
        null,
      ),
      supplier: new UntypedFormControl(orderImportDetail.supplier, null),
      fixed_price: new UntypedFormControl(orderImportDetail.fixed_price, null),
      task_sequence: new UntypedFormControl(orderImportDetail.task_sequence, null),
    });
  }

  saveOrderDetails(orderDetail: IOrderImportDetail) {
    this.isBusy = true;
    this.orderImportService
      .saveImportDetail(orderDetail.id, orderDetail)
      .then((res) => {
        this.isBusy = this.showOrderUpdateDialog = false;
        if (res) this.handleUpdateImportDetail(this.selectedOrderImport.id, orderDetail);
      })
      .catch((err) => {
        this.isBusy = this.showOrderUpdateDialog = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Updating Order Details',
          detail: `An error occurred while updating order detail ${err && err.error}`,
        });
      });
  }

  hideDialog() {
    this.isBusy = false;
    this.isProcessing = false;
  }

  parseValidationString(validationString: string): any[] {
    try {
      return JSON.parse(validationString);
    } catch (e) {
      return [];
    }
  }

  canDeleteImport(orderImport: IOrderImport): boolean {
    return !(
      orderImport.status.id === OrderImportStatus.Queued ||
      orderImport.status.id === OrderImportStatus.ProcessingForTransfer ||
      orderImport.status.id === OrderImportStatus.Transferred
    );
  }

  canValidateImport(orderImport: IOrderImport): boolean {
    return (
      orderImport.status.id === OrderImportStatus.New ||
      orderImport.status.id === OrderImportStatus.Invalid ||
      orderImport.status.id === OrderImportStatus.ReadyForTransfer
    );
  }

  canProcessOrderImport(orderImport: IOrderImport): boolean {
    return (
      orderImport.status.id === OrderImportStatus.ReadyForTransfer ||
      orderImport.status.id === OrderImportStatus.TransferError
    );
  }

  canEditRemoveImportLine(importLine: IOrderImportDetail, orderImport: IOrderImport): boolean {
    return (
      (importLine.status_id === OrderImportStatus.New ||
        importLine.status_id === OrderImportStatus.Valid ||
        importLine.status_id === OrderImportStatus.Invalid ||
        importLine.status_id === OrderImportStatus.TransferError ||
        importLine.status_id === OrderImportStatus.ReadyForTransfer) &&
      (orderImport.status.id === OrderImportStatus.New ||
        orderImport.status.id === OrderImportStatus.Valid ||
        orderImport.status.id === OrderImportStatus.Invalid ||
        orderImport.status.id === OrderImportStatus.TransferError ||
        orderImport.status.id === OrderImportStatus.ReadyForTransfer)
    );
  }

  changeSelectedOrder(orderImport: IOrderImport) {
    this.selectedOrderImport = orderImport;
    this.getOrderDetails(orderImport.id);
  }

  getStatusIcon(statusId: OrderImportStatus): { icon: string; color: string } {
    switch (statusId) {
      case OrderImportStatus.Valid:
      case OrderImportStatus.ReadyForTransfer:
      case OrderImportStatus.Transferred:
        return {
          icon: 'fa fa-check-circle',
          color: '#28a745',
        };

      case OrderImportStatus.Invalid:
      case OrderImportStatus.TransferError:
        return {
          icon: 'fa fa-times-circle',
          color: '#dc3545',
        };

      case OrderImportStatus.Validating:
      case OrderImportStatus.ProcessingForTransfer:
      case OrderImportStatus.Queued:
        return {
          icon: 'fa fa-circle-o-notch fa-spin',
          color: '#17a2b8',
        };

      case OrderImportStatus.New:
        return {
          icon: 'fa fa-check-circle-o',
          color: '#6c757d',
        };

      case OrderImportStatus.Archived:
        return {
          icon: 'fa fa-archive',
          color: '#6c757d',
        };

      default:
        return {
          icon: 'fa fa-question-circle',
          color: '#6c757d',
        };
    }
  }

  showSpinnerOnImportCard(orderImport: IOrderImport): boolean {
    return (
      orderImport.status.id === OrderImportStatus.Queued ||
      orderImport.status.id === OrderImportStatus.ProcessingForTransfer
    );
  }
}
